import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { investmentCreateSchema } from "features/investments/InvestmentSchemas";
import { PortfolioFormRequiredFields } from "features/investments/portfolio/PortfolioFormFields";
import { useInvestmentCreate } from "gen/endpoints/investment/investment";
import { InvestmentEntityCreateEditAPIDataRequest } from "gen/model/investmentEntityCreateEditAPIDataRequest";
import { InvestmentEntityReadAPIData } from "gen/model/investmentEntityReadAPIData";
import { InvestmentTypeEnum } from "gen/model/investmentTypeEnum";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

const AddPortfolioModal: React.FC<{
  investmentType?: InvestmentTypeEnum;
  onCloseComplete: () => void;
  onSuccess: (portfolio: InvestmentEntityReadAPIData) => void;
  initialName?: string;
}> = ({ investmentType, initialName, onCloseComplete, onSuccess }) => {
  const { t } = useTranslation(["common", "investment"]);

  const { fundFamilyId } = useRequiredStringParams();
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const { mutateAsync: createInvestment } = useInvestmentCreate();
  const form = useForm<InvestmentEntityCreateEditAPIDataRequest>({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(investmentCreateSchema),
    defaultValues: {
      legal_name: initialName,
      legal_type: "non_legal_entity",
      metadata: {
        investment_or_portfolio: "portfolio",
        investment_type: investmentType
          ? investmentType
          : InvestmentTypeEnum.equity,
      },
    },
  });

  const legalName = form.watch("legal_name");
  useEffect(() => {
    form.setValue("external_id", legalName);
  }, [form, legalName]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("investment:addPortfolio.title")}</ModalHeader>
        <Form
          form={form}
          onSubmit={async (data: InvestmentEntityCreateEditAPIDataRequest) =>
            await createInvestment({
              fundFamilyId,
              data,
            })
          }
          onSuccess={(portfolio: InvestmentEntityReadAPIData) => {
            onSuccess(portfolio);
            onClose();
          }}
          successMessage={t("investment:addPortfolio.success")}
        >
          <ModalBody>
            <PortfolioFormRequiredFields />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onCloseComplete} />}
              confirm={
                <FormSubmitButton>
                  {t("investment:addPortfolio.button")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AddPortfolioModal;
